// SF-114 / Prompt 81 — Two-mode Credit Note entry form (shared everywhere: Finance >
// Payment subtype, supplier detail, GIT inline sub-tab, OperationDetailView, Payables).
// Posts to POST /api/credit-notes with a `mode` param:
//   mode='apply_to_goods'  (subtype='credit_note')    — open orders only; lowers unit
//                                                        price on open lines + reduces GIT.
//   mode='supplier_credit' (subtype='supplier_credit') — any order; reduces AP, posts the
//                                                        offset to the Supplier Credit
//                                                        contra-expense account.
// A journal-entry preview shows the real accounts + amounts for the selected mode.
import { formatDate } from '../../utils/format';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../services/api';
import HelpIcon from '../HelpIcon';
import Combobox from '../Combobox';
import { isOpen } from '../../lib/deliveryStatus';
import { ITEM_CATEGORY_LABELS } from '../../config/categoryConstants';   // 466 W8: live label, never raw key
import { labelFor } from '../../utils/labels';
import useActionLabel from '../../i18n/useActionLabel';
import { lineText } from '../../shell/ActionIcons';
import { emoText } from '../../shell/EmojiIcons';
import EnumSelect from '../EnumSelect';
import { AmountInput, TextInput } from '../inputs/Input';

const fmt = (n) => (Number(n) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);

export default function CreditNoteForm({ initialPartnerId = '', initialOperationId = '', initialMode = 'apply_to_goods', onSuccess, lockPartner = false, lockOrder = false }) {
  const al = useActionLabel();
  // Prompt 197: context lock. lockPartner (supplier page) → supplier fixed, order list filtered to it.
  // lockOrder (order context) → supplier AND order fixed. Locked selects render greyed/disabled.
  const [partners,   setPartners]   = useState([]);
  const [purchases,  setPurchases]  = useState([]);
  const [finance,    setFinance]    = useState([]);   // finance rows — enumerate landed-cost suppliers
  const [git,        setGit]        = useState([]);   // goods_in_transit (open lines, with qty_remaining)
  const [itemsMap,   setItemsMap]   = useState({});
  const [filterSupplierId, setFilterSupplierId] = useState(initialPartnerId); // op-list filter only
  const [partnerId,  setPartnerId]  = useState(initialPartnerId);             // routing partner (chooser)
  const [operationId,setOperationId]= useState(initialOperationId);
  const [openOnly,   setOpenOnly]   = useState(false);   // filter the order list to open orders only
  const [mode,       setMode]       = useState(initialMode);
  const [date,       setDate]       = useState(today());
  const [amount,     setAmount]     = useState('');
  const [cashFork,   setCashFork]   = useState(false);   // Prompt 200: cash order needs refund/writeoff
  const [paidChoice, setPaidChoice] = useState('');       // 'refund_receivable' | 'writeoff'
  const [method,     setMethod]     = useState('by_value');
  const [notes,      setNotes]      = useState('');
  const [reason,     setReason]     = useState('');   // 208: optional §9 reason threaded into the POST body
  const [submitting, setSubmitting] = useState(false);
  const [msg,        setMsg]        = useState(null); // { type, text }
  const [lcRows,     setLcRows]     = useState([]);   // landed_costs of the selected order (real LC partner)
  const [selectedLcId, setSelectedLcId] = useState('');   // 3b: which LC to credit (apply_to_lc mode)
  const [lcBase,     setLcBase]     = useState(null);   // 3b: adjust-impact base for the selected LC

  useEffect(() => {
    api.getPartners().then(ps => setPartners((ps || []).filter(p => p.type === 'Supplier'))).catch(() => {});
    api.request('/purchase?tx_status=posted').then(r => setPurchases(r || [])).catch(() => {});
    api.getFinance().then(f => setFinance(f || [])).catch(() => {});
    api.getPlannedTransactions({}).then(d => setGit(d?.goods_in_transit || [])).catch(() => {});
    api.request('/items').then(items => { const m = {}; (items || []).forEach(i => { m[i.id] = i; }); setItemsMap(m); }).catch(() => {});
  }, []);

  // LC finance legs are NULL-partner (the real supplier is on landed_costs.partner_id), so the raw
  // finance scan below misses the landed-cost supplier. Fetch the selected order's landed_costs to
  // surface it — same reason the ledger-based payment panel shows Supplier C but the raw scan didn't.
  useEffect(() => {
    if (!operationId) { setLcRows([]); return; }
    let live = true;
    api.request(`/landed-costs?operation_id=${encodeURIComponent(operationId)}`)
      .then(d => { if (live) setLcRows(d?.landed_costs || []); }).catch(() => { if (live) setLcRows([]); });
    return () => { live = false; };
  }, [operationId]);

  const itemName = useCallback((id) => itemsMap[id]?.name || id, [itemsMap]);
  const partnerName = useMemo(() => partners.find(p => p.id === partnerId)?.name || 'Supplier', [partners, partnerId]);
  const partnerNameOf = (id) => partners.find(p => p.id === id)?.name || id || '—';

  // ── Prompt 96 (Part 2.1/2.2): enumerate ALL suppliers on each order — the UNION of
  // goods partners (purchase_detail.partner_id) AND landed-cost partners (finance
  // subtype='landed_cost' partner_id). Each supplier carries its own amount: goods =
  // Σ goods finance base ex-LC; landed-cost supplier = its landed_cost amount.
  const opMap = useMemo(() => {
    const ops = {};
    const ensure = (opId, date) => {
      if (!ops[opId]) ops[opId] = { operation_id: opId, date: date || '', items: new Set(), open: false, suppliers: new Map() };
      if (date && (!ops[opId].date || date < ops[opId].date)) ops[opId].date = date;
      return ops[opId];
    };
    const sup = (op, pid) => {
      if (!op.suppliers.has(pid)) op.suppliers.set(pid, { partner_id: pid, isGoods: false, isLanded: false, goodsAmount: 0, landedAmount: 0 });
      return op.suppliers.get(pid);
    };
    // Goods membership + item/open flags from purchase_detail lines.
    for (const p of purchases) {
      if (!p.operation_id) continue;
      const op = ensure(p.operation_id, p.date);
      op.items.add(itemsMap[p.item_id]?.name || '');
      if (isOpen(p)) op.open = true;   // Prompt 163: route to deliveryStatus.isOpen (≡ en_route|partial)
      if (p.partner_id) sup(op, p.partner_id).isGoods = true;
    }
    // Amounts from finance rows: goods base (ex-LC) vs landed_cost.
    for (const f of finance) {
      if (f.type !== 'Purchase' || f.is_system || !f.operation_id || !f.partner_id) continue;
      if (f.subtype === 'landed_cost') {
        const op = ops[f.operation_id] || ensure(f.operation_id, f.date);
        const s = sup(op, f.partner_id); s.isLanded = true; s.landedAmount += Number(f.amount) || 0;
      } else if (f.subtype !== 'credit_note' && f.subtype !== 'supplier_credit') {
        const op = ops[f.operation_id];
        if (op) sup(op, f.partner_id).goodsAmount += Number(f.amount) || 0;
      }
    }
    return ops;
  }, [purchases, finance, itemsMap]);

  // Operations dropdown — optionally filtered by the supplier filter (op must include that
  // supplier as goods OR landed). No filter → all orders. Newest first.
  const operations = useMemo(() => {
    return Object.values(opMap)
      .filter(op => !filterSupplierId || op.suppliers.has(filterSupplierId))
      .filter(op => !openOnly || op.open)
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [opMap, filterSupplierId, openOnly]);

  // Prompt 214 (bug: "order not pre-selected from Current Payables"). When the panel is opened
  // for a specific order (lockOrder, or any pre-set operationId), the controlled <select> shows
  // blank unless a matching <option> exists. The op can be missing from `operations` because (a)
  // the async purchase/finance fetch hasn't landed yet, or (b) the supplier/open filters exclude
  // it. Guarantee the selected order is always present as an option so the prefill actually shows.
  const orderOptions = useMemo(() => {
    if (!operationId || operations.some(o => o.operation_id === operationId)) return operations;
    const known = opMap[operationId];
    const pinned = known || { operation_id: operationId, items: new Set(), date: '', open: false };
    return [pinned, ...operations];
  }, [operations, operationId, opMap]);

  const selectedOp = operationId ? opMap[operationId] : null;
  const fullyReceived = !!selectedOp && !selectedOp.open;  // no open (en_route/partial) lines left

  // The op's suppliers, each with display name + total amount, goods first.
  const opSuppliers = useMemo(() => {
    if (!selectedOp) return [];
    const m = new Map();
    for (const s of selectedOp.suppliers.values()) m.set(s.partner_id, { ...s });
    // Merge LC suppliers from landed_costs (finance LC legs are NULL-partner → missed by the scan).
    for (const lc of lcRows) {
      const pid = lc.partner_id; if (!pid) continue;
      if (!m.has(pid)) m.set(pid, { partner_id: pid, isGoods: false, isLanded: false, goodsAmount: 0, landedAmount: 0 });
      const e = m.get(pid); e.isLanded = true; e.landedAmount += Number(lc.amount) || 0;
    }
    // Always show ALL of the order's suppliers so you can credit either — the supplier filter only
    // pre-selects one (below), it does NOT hide the others.
    return [...m.values()]
      .map(s => ({ ...s, name: partnerNameOf(s.partner_id), amount: s.goodsAmount + s.landedAmount }))
      .sort((a, b) => (Number(b.isGoods) - Number(a.isGoods)) || (b.amount - a.amount));
  }, [selectedOp, partners, lcRows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default the routing partner to a supplier that actually belongs to the op. Validity is checked
  // against opSuppliers (which includes the LC supplier), so an LC-only pick isn't clobbered. When a
  // supplier filter is set, it scopes the credit → route to that supplier.
  useEffect(() => {
    if (!selectedOp || !opSuppliers.length) return;
    // Keep the current pick if it's valid — so clicking either tile sticks even with a filter set.
    if (partnerId && opSuppliers.some(s => s.partner_id === partnerId)) return;
    // No valid pick yet → pre-select: the filtered supplier, else prefill, else goods, else first.
    const pick = (filterSupplierId && opSuppliers.some(s => s.partner_id === filterSupplierId) && filterSupplierId)
      || (initialPartnerId && opSuppliers.some(s => s.partner_id === initialPartnerId) && initialPartnerId)
      || opSuppliers.find(s => s.isGoods)?.partner_id
      || opSuppliers[0]?.partner_id || '';
    if (pick) setPartnerId(pick);
  }, [operationId, selectedOp, opSuppliers, filterSupplierId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selSupplier = opSuppliers.find(s => s.partner_id === partnerId) || null;
  // Mode-1 (apply_to_goods) is valid ONLY for a GOODS supplier on the op (mirrors the
  // backend 422). A landed-cost-only supplier is forced to Mode 2 (supplier_credit).
  const landedOnly = !!selSupplier && selSupplier.isLanded && !selSupplier.isGoods;
  const mode1Blocked = fullyReceived || landedOnly;

  // 3b: the selected supplier's landed costs on this order (the LC counterpart of Mode-1).
  const supplierLcs = useMemo(() => lcRows.filter(lc => lc.partner_id === partnerId), [lcRows, partnerId]);
  const canApplyLc = supplierLcs.length > 0;

  const openLines = useMemo(() => git.filter(g => g.operation_id === operationId), [git, operationId]);

  // Mode routing: a landed-cost supplier defaults to 'apply_to_lc' (reduce the LC's holding + AP);
  // if it has no creditable LC, or the order is fully received on the goods side, fall to Supplier Credit.
  useEffect(() => {
    if (landedOnly && canApplyLc && mode === 'apply_to_goods') setMode('apply_to_lc');
    else if (mode1Blocked && !canApplyLc && mode !== 'supplier_credit') setMode('supplier_credit');
    else if (mode === 'apply_to_lc' && !canApplyLc) setMode('supplier_credit');
  }, [mode1Blocked, landedOnly, canApplyLc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default the LC to credit to the supplier's first LC; refresh its adjust-impact base (holding,
  // nature, fully-absorbed) whenever it changes so we can cap the credit and detect the cash fork.
  useEffect(() => {
    if (!canApplyLc) { setSelectedLcId(''); setLcBase(null); return; }
    if (!supplierLcs.some(lc => lc.id === selectedLcId)) setSelectedLcId(supplierLcs[0].id);
  }, [supplierLcs]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!selectedLcId) { setLcBase(null); return; }
    let live = true;
    api.getLcAdjustImpact(selectedLcId, 0).then(d => { if (live) setLcBase(d || null); }).catch(() => { if (live) setLcBase(null); });
    return () => { live = false; };
  }, [selectedLcId]);

  const isApply = mode === 'apply_to_goods';
  const isApplyLc = mode === 'apply_to_lc';

  // Apply-to-goods reactive preview: per-line reduction, mirrors the backend allocation.
  const applyPreview = useMemo(() => {
    const amt = parseFloat(amount);
    if (!isApply || !amt || amt <= 0 || openLines.length === 0) return null;
    const rows = [];
    if (method === 'by_quantity') {
      const totalQty = openLines.reduce((s, l) => s + (Number(l.qty_remaining) || 0), 0);
      const perUnit = totalQty > 0 ? amt / totalQty : 0;
      for (const l of openLines) {
        const qr = Number(l.qty_remaining) || 0, cur = Number(l.unit_price) || 0;
        rows.push({ name: l.item_name || itemName(l.item_id), item_id: l.item_id, qtyOpen: qr, cur, reduction: perUnit, np: Math.max(0, cur - perUnit) });
      }
    } else {
      const totalVal = openLines.reduce((s, l) => s + (Number(l.qty_remaining) || 0) * (Number(l.unit_price) || 0), 0);
      for (const l of openLines) {
        const qr = Number(l.qty_remaining) || 0, cur = Number(l.unit_price) || 0;
        const reduction = (totalVal > 0 && qr > 0) ? (amt * ((qr * cur) / totalVal)) / qr : 0;
        rows.push({ name: l.item_name || itemName(l.item_id), item_id: l.item_id, qtyOpen: qr, cur, reduction, np: Math.max(0, cur - reduction) });
      }
    }
    const catSplit = {};
    for (const r of rows) { const cat = itemsMap[r.item_id]?.category || '—'; catSplit[cat] = (catSplit[cat] || 0) + r.reduction * r.qtyOpen; }
    return { rows, total: amt, catSplit };
  }, [amount, method, openLines, isApply, itemsMap, itemName]);

  // Mixed-unit guard: "By Quantity" allocation is invalid when the order spans >1 unit (e.g. kg + dose)
  // — you can't distribute a credit by quantity across different units. Disable it + auto-switch to By Value.
  const cnMixedUnit = useMemo(() => {
    const units = new Set((openLines || []).map(l => (itemsMap[l.item_id]?.unit || '').trim()).filter(Boolean));
    return units.size > 1;
  }, [openLines, itemsMap]);
  useEffect(() => { if (cnMixedUnit && method === 'by_quantity') setMethod('by_value'); }, [cnMixedUnit]); // eslint-disable-line react-hooks/exhaustive-deps

  const amt = parseFloat(amount) || 0;
  const lcHolding = Number(lcBase?.prev_remaining) || 0;   // creditable ceiling for apply_to_lc
  const lcOverCap = isApplyLc && amt > lcHolding + 0.005;
  const canSubmit = partnerId && operationId && amt > 0 && !submitting
    && !(isApplyLc && (!selectedLcId || lcOverCap || lcHolding <= 0.01));

  async function submit(e) {
    e && e.preventDefault();
    if (submitting) return;
    if (!partnerId || !operationId || !amount) { setMsg({ type: 'error', text: 'Supplier, operation and amount are required.' }); return; }
    setSubmitting(true); setMsg(null);
    try {
      // 3b — Apply to landed cost: reduce THIS LC's remaining holding by the credit amount (holding +
      // AP both drop). Reuses /landed-costs/adjust; a cash-paid LC returns LC_CASH_PAID_FORK → the
      // same refund/writeoff fork as the goods cash order.
      if (isApplyLc) {
        if (lcOverCap) { setMsg({ type: 'error', text: `Maximum creditable on this landed cost is ${fmt(lcHolding)} F.` }); setSubmitting(false); return; }
        const new_remaining = Math.round((lcHolding - amt) * 100) / 100;
        const { ok, status, data } = await api.adjustLandedCost({ landed_cost_id: selectedLcId, new_remaining, paid_choice: paidChoice || undefined, reason: reason || undefined });
        if (!ok) {
          if (status === 422 && data?.code === 'LC_CASH_PAID_FORK') { setCashFork(true); setMsg({ type: 'warning', text: data.message || 'This landed cost was paid in cash — choose refund or write-off.' }); return; }
          setMsg({ type: 'error', text: data?.error || 'Landed-cost credit failed.' });
          return;
        }
        setCashFork(false); setPaidChoice('');
        setMsg({ type: 'success', text: `:check: Landed-cost credit of ${fmt(amt)} F applied — holding and payable reduced.` });
        setAmount('');
        api.getLcAdjustImpact(selectedLcId, 0).then(d => setLcBase(d || null)).catch(() => {});
        onSuccess && onSuccess(data);
        return;
      }
      const body = { operation_id: operationId, partner_id: partnerId, date, amount: parseFloat(amount), notes: notes || null, mode, reason: reason || undefined };
      if (isApply) body.allocation_method = method;
      if (paidChoice) body.paid_choice = paidChoice;   // Prompt 200: cash-order §3 fork
      // requestRaw so we can surface the engine's structured 422 gracefully — the per-supplier
      // cap returns { cap }, and "no supplier account for {partner}" arrives as { error }.
      const { ok, status, data } = await api.requestRaw('/credit-notes', { method: 'POST', body: JSON.stringify(body) });
      if (!ok) {
        // Prompt 200: cash order (no AP) — the supplier owes a refund. Surface the §3 paid fork
        // (refund/writeoff) inline and let the user re-submit with a choice, rather than erroring.
        if (status === 422 && data?.code === 'CASH_ORDER_PAID_FORK') {
          setCashFork(true);
          setMsg({ type: 'warning', text: data.error });
          return;
        }
        let text = data?.error || 'Credit note failed.';
        if (status === 422 && data?.cap != null) text += ` — maximum allowed is ${fmt(data.cap)} F.`;
        setMsg({ type: 'error', text });
        return;
      }
      setCashFork(false); setPaidChoice('');
      const label = data.mode === 'supplier_credit' ? 'Supplier Credit'
        : data.mode === 'cash_paid_fork' ? (data.paid_choice === 'writeoff' ? 'Written off (P&L loss)' : 'Refund receivable')
        : 'Applied to goods';
      const warn = (data.warnings || []).join(' ');
      setMsg({ type: warn ? 'warning' : 'success', text: `:check: Credit note of ${fmt(parseFloat(amount))} F posted — ${label}.${warn ? ' ' + warn : ''}` });
      setAmount('');
      onSuccess && onSuccess(data);
    } catch (err) {
      setMsg({ type: 'error', text: err?.message || 'Credit note failed.' });
    } finally { setSubmitting(false); }
  }

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ fontSize: 12, color: '#0369a1', background: '#e0f2fe', borderRadius: 6, padding: '8px 12px', marginBottom: 14 }}>
        A credit note reduces what you owe a supplier, with no cash moving. Pick how to record it below — the preview shows the exact accounting before you confirm.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
        <label style={fg}><span style={lbl}>Supplier filter{(lockPartner || lockOrder) && ' (locked)'}</span>
          <EnumSelect bare testid="cn-supplier" value={filterSupplierId} disabled={lockPartner || lockOrder} onChange={val => {
              const v = val;
              setFilterSupplierId(v); setPartnerId('');
              // Bidirectional filter: only clear the chosen order if the new supplier isn't on it.
              if (v && operationId && opSuppliers.length && !opSuppliers.some(s => s.partner_id === v)) setOperationId('');
            }}>
            <option value="">All suppliers</option>
            {/* Narrow to the selected order's suppliers (goods + LC, via opSuppliers which merges
                landed_costs) when an order is picked — the reverse filter. */}
            {(operationId && opSuppliers.length ? opSuppliers.map(s => ({ id: s.partner_id, name: s.name })) : partners)
              .map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </EnumSelect>
        </label>
        <label style={{ ...fg, gridColumn: 'span 2' }}>
          <span style={{ ...lbl, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Order *{lockOrder && ' (locked)'}</span>
            {!lockOrder && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 400, color: 'var(--muted)', cursor: 'pointer', textTransform: 'none', letterSpacing: 0 }}>
                <input type="checkbox" checked={openOnly} onChange={e => { const v = e.target.checked; setOpenOnly(v); if (v && selectedOp && !selectedOp.open) setOperationId(''); }} /> Open only
              </label>
            )}
          </span>
          {lockOrder ? (
            <EnumSelect bare testid="cn-order" value={operationId} disabled>
              <option value={operationId}>{operationId}</option>
            </EnumSelect>
          ) : (
            <div data-testid="cn-order">
              <Combobox
                options={orderOptions.map(o => ({
                  value: o.operation_id,
                  label: `${o.operation_id} · ${[...o.items].filter(Boolean).join(', ')} · ${formatDate(o.date)} · ${o.open ? 'open' : 'received'}`,
                }))}
                value={operationId}
                onChange={setOperationId}
                placeholder="Select order… (search)"
                style={{ display: 'block', width: '100%' }}
              />
            </div>
          )}
        </label>
        <label style={fg}><span style={lbl}>Date *</span>
          <TextInput type="date" value={date} onChange={e => setDate(e.target.value)} style={inp} />
        </label>
        <label style={fg}><span style={lbl}>Amount *</span>
          <AmountInput data-testid="cn-amount" min="0" step="any" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" style={inp} />
        </label>
        {isApply && (
          <label style={fg}><span style={lbl}>Allocation</span>
            <EnumSelect bare value={method} onChange={v => setMethod(v)}>
              <option value="by_value">By Value</option>
              <option value="by_quantity" disabled={cnMixedUnit}>By Quantity{cnMixedUnit ? ' — mixed units' : ''}</option>
            </EnumSelect>
            {cnMixedUnit && <span style={{ fontSize: 11, color: '#92400e', marginTop: 3, display: 'block' }}>Order has mixed units — By Quantity is unavailable; using By Value.</span>}
          </label>
        )}
        <label style={{ ...fg, gridColumn: 'span 2' }}><span style={lbl}>Notes</span>
          <TextInput type="text" value={notes} onChange={e => setNotes(e.target.value)} style={inp} />
        </label>
        <label style={{ ...fg, gridColumn: 'span 2' }}><span style={lbl}>Reason (optional)</span>
          <TextInput type="text" value={reason} onChange={e => setReason(e.target.value)} style={inp} />
        </label>
      </div>

      {/* A credit is always issued FOR a specific order, so there is no blind oldest-first settlement —
          the FIFO waterfall was removed. Mode-1 reprices this order's open goods lines in place;
          apply_to_lc reduces this order's landed-cost holding + AP; Mode-2 is order-tied (reduces THIS
          order first) with only the excess beyond this order's own balance spilling to older orders. */}
      {partnerId && operationId && (
        <div style={{ marginTop: 12, border: '1px solid var(--line)', borderRadius: 6, background: '#fbfdfb', padding: '8px 12px', fontSize: 11, color: 'var(--muted)' }}>
          {isApply
            ? <>This credit applies <strong>directly to {operationId}</strong>’s goods invoice — it lowers the unit cost of the goods still in transit. Not settled oldest-first.</>
            : isApplyLc
              ? <>This credit reduces <strong>{operationId}</strong>’s landed cost — its holding (the cost bound for inventory) and what you owe the LC supplier both drop. Per landed cost; not oldest-first.</>
              : <>This supplier credit reduces <strong>{operationId}</strong>’s balance <strong>first</strong>. Only an amount larger than this order’s own balance spills to the supplier’s oldest open orders.</>}
        </div>
      )}

      {/* ── SUPPLIER CHOOSER (Prompt 96 Part 2.2): all suppliers on the order, each with
           their own amount; the pick routes the credit to that partner_id. ── */}
      {operationId && opSuppliers.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--muted)', marginBottom: 6 }}>
            Supplier to credit *
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 10 }}>
            {opSuppliers.map(s => {
              const active = s.partner_id === partnerId;
              // Only lockPartner fixes WHICH supplier you credit. lockOrder fixes the order but you can
              // still choose goods vs landed-cost supplier on it (so an LC can be credited from an order).
              const tileLocked = lockPartner;
              return (
                <div key={s.partner_id} role="button" tabIndex={tileLocked ? -1 : 0}
                  onClick={() => { if (tileLocked) return; setPartnerId(s.partner_id); setMsg(null); }}
                  onKeyDown={e => { if (tileLocked) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPartnerId(s.partner_id); setMsg(null); } }}
                  style={{ textAlign: 'left', border: active ? '2px solid #1b4332' : '1px solid var(--line)', borderRadius: 8,
                    padding: '9px 12px', background: active ? '#f0fdf4' : '#fff', cursor: tileLocked ? 'not-allowed' : 'pointer', opacity: tileLocked && !active ? 0.5 : 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{s.name}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(s.amount)} F</span>
                  </div>
                  <div style={{ marginTop: 4, display: 'flex', gap: 5 }}>
                    {s.isGoods  && <span style={roleBadge('#1b4332', '#e7f2ec')}>Goods</span>}
                    {s.isLanded && <span style={roleBadge('#c47d10', '#fdf3e2')}>Landed cost</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── MODE TOGGLE ── (cards depend on what the chosen supplier is on this order: goods → Apply
           to goods; landed cost → Apply to landed cost; both may show; Supplier Credit always). */}
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--muted)', marginBottom: 6 }}>How to record it</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10 }}>
          {selSupplier?.isGoods && !fullyReceived && (
            <ModeCard
              active={isApply} disabled={false}
              title="Apply to goods" helpKey="concept.credit_note_apply_to_goods"
              desc="Lowers unit cost of goods still in transit. Reduces invoice. Open orders only."
              onClick={() => setMode('apply_to_goods')}
            />
          )}
          {canApplyLc && (
            <ModeCard
              active={isApplyLc} disabled={false}
              title="Apply to landed cost" helpKey="concept.credit_note_apply_to_goods"
              desc="Reduces this landed cost's remaining holding AND what you owe the LC supplier. Per landed cost."
              onClick={() => setMode('apply_to_lc')}
            />
          )}
          <ModeCard
            active={mode === 'supplier_credit'} disabled={false}
            title="Record as Supplier Credit" helpKey="concept.credit_note_supplier_credit"
            desc="Reduces what you owe, recorded as a cost reduction. Invoice unchanged. Any order."
            onClick={() => setMode('supplier_credit')}
          />
        </div>
        {fullyReceived && !canApplyLc && (
          <div style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '7px 12px', marginTop: 10 }}>
            Order fully received — credit recorded as Supplier Credit.
          </div>
        )}
      </div>

      {/* ── LC SELECTOR (apply_to_lc): pick which landed cost, show its creditable holding. ── */}
      {isApplyLc && canApplyLc && (
        <div style={{ marginTop: 12, border: '1px solid var(--line)', borderRadius: 6, background: '#fbfdfb', padding: '10px 12px' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {supplierLcs.length > 1 && (
              <label style={fg}><span style={lbl}>Landed cost</span>
                <EnumSelect bare value={selectedLcId} onChange={v => setSelectedLcId(v)}>
                  {supplierLcs.map(lc => <option key={lc.id} value={lc.id}>LC {String(lc.id).slice(0, 8)} · {fmt(Number(lc.amount) || 0)} F</option>)}
                </EnumSelect>
              </label>
            )}
            <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
              Creditable (remaining holding): <strong style={{ color: 'var(--ink)' }}>{fmt(lcHolding)} F</strong>
              {lcBase?.nature === 'cash' && <span style={{ color: '#c47d10' }}> · paid in cash (refund/write-off on submit)</span>}
              {lcBase && lcHolding <= 0.01 && <span style={{ color: '#dc2626' }}> · fully absorbed — nothing to credit</span>}
            </div>
          </div>
          {lcOverCap && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 6 }}>Amount exceeds the creditable holding ({fmt(lcHolding)} F).</div>}
        </div>
      )}

      {/* ── JOURNAL-ENTRY PREVIEW ── */}
      {amt > 0 && operationId && (
        <div style={{ marginTop: 16, border: '1px solid var(--line)', borderRadius: 8, padding: '12px 14px', background: 'var(--card)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8, display: 'flex', alignItems: 'center' }}>
            Journal preview <HelpIcon helpKey="concept.credit_note_journal_preview" />
          </div>

          {isApply ? (
            <>
              {applyPreview ? (
                <>
                  {Object.entries(applyPreview.catSplit).map(([cat, v]) => (
                    <div key={cat} style={je}>
                      <span><strong>DR</strong> Supplier:{partnerName}</span><span style={jeAmt}>{fmt(v)} F</span>
                    </div>
                  ))}
                  {Object.entries(applyPreview.catSplit).map(([cat, v]) => (
                    <div key={'cr' + cat} style={{ ...je, color: '#166534' }}>
                      <span style={{ paddingLeft: 18 }}><strong>CR</strong> GIT:{labelFor(ITEM_CATEGORY_LABELS, cat)}</span><span style={jeAmt}>{fmt(v)} F</span>
                    </div>
                  ))}
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--muted)', margin: '12px 0 6px' }}>Unit price reduction</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead><tr>
                      {['Item', 'Qty Open', 'Current Price', 'Reduction/Unit', 'New Price'].map((h, i) => (
                        <th key={h} style={{ textAlign: i === 0 ? 'left' : 'right', padding: '6px 8px', borderBottom: '1px solid var(--line)', color: 'var(--muted)', fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {applyPreview.rows.map((r, i) => (
                        <tr key={i}>
                          <td style={td}>{r.name}</td>
                          <td style={tdr}>{fmt(r.qtyOpen)}</td>
                          <td style={tdr}>{fmt(r.cur)} F</td>
                          <td style={{ ...tdr, color: '#dc2626' }}>−{fmt(r.reduction)} F</td>
                          <td style={{ ...tdr, fontWeight: 700 }}>{fmt(r.np)} F</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
                    Effect: reduces the invoice and Goods-In-Transit value. No profit-and-loss impact yet — the goods simply cost less.
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Enter an amount to preview the per-category and unit-price effect.</div>
              )}
            </>
          ) : isApplyLc ? (
            <>
              <div style={je}><span><strong>DR</strong> Supplier:{partnerName}</span><span style={jeAmt}>{fmt(amt)} F</span></div>
              <div style={{ ...je, color: '#166534' }}><span style={{ paddingLeft: 18 }}><strong>CR</strong> LC Holding</span><span style={jeAmt}>{fmt(amt)} F</span></div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
                Effect: reduces this landed cost’s remaining holding (less cost lands in inventory) and what you owe the LC supplier.
                {lcBase?.nature === 'cash' ? ' Paid in cash — you’ll choose refund-receivable or write-off on submit.' : ''}
              </div>
            </>
          ) : (
            <>
              <div style={je}><span><strong>DR</strong> Supplier:{partnerName}</span><span style={jeAmt}>{fmt(amt)} F</span></div>
              <div style={{ ...je, color: '#166534' }}><span style={{ paddingLeft: 18 }}><strong>CR</strong> Supplier Credit</span><span style={jeAmt}>{fmt(amt)} F</span></div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
                Effect: reduces amount owed, recorded as a cost reduction. Invoice unchanged.
              </div>
            </>
          )}
        </div>
      )}

      {msg && (
        <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 6, fontSize: 12.5,
          background: msg.type === 'error' ? '#fee2e2' : msg.type === 'warning' ? '#fef3c7' : '#dcfce7',
          color: msg.type === 'error' ? '#dc2626' : msg.type === 'warning' ? '#92400e' : '#166534' }}>
          {emoText(msg.text)}
        </div>
      )}

      {/* Prompt 200: cash order (already paid) → the supplier owes a refund. Pick the §3 disposition. */}
      {cashFork && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>Cash order — how to resolve the {fmt(amt)} F the supplier owes back?</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#78350f', marginBottom: 4, cursor: 'pointer' }}>
            <input data-testid="cn-refund" type="radio" name="cnpaid" checked={paidChoice === 'refund_receivable'} onChange={() => setPaidChoice('refund_receivable')} />
            Refund receivable — the supplier owes it back (their account goes negative)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#78350f', cursor: 'pointer' }}>
            <input data-testid="cn-writeoff" type="radio" name="cnpaid" checked={paidChoice === 'writeoff'} onChange={() => setPaidChoice('writeoff')} />
            Write off — treat the credit as a loss (expense)
          </label>
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <button className="btn success sm" type="button" data-testid="cn-submit" onClick={submit} disabled={!canSubmit || (cashFork && !paidChoice)}
          style={{ opacity: (canSubmit && !(cashFork && !paidChoice)) ? 1 : 0.6 }}>
          {submitting ? 'Posting…' : cashFork ? 'Confirm & post' : al('recordSupplierCreditNote')}
        </button>
      </div>
    </div>
  );
}

function ModeCard({ active, disabled, title, desc, helpKey, onClick }) {
  // div (not button) so the HelpIcon <button> can nest without invalid DOM nesting;
  // clicks on the help icon call stopPropagation so they don't toggle the mode.
  return (
    <div role="button" tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onClick()}
      onKeyDown={e => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
      style={{ textAlign: 'left', border: active ? '2px solid #1b4332' : '1px solid var(--line)', borderRadius: 8,
        padding: '10px 12px', background: active ? '#f0fdf4' : disabled ? '#f8f9f8' : '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center' }}>
        {lineText(title)} <HelpIcon helpKey={helpKey} />
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3, lineHeight: 1.4 }}>{desc}</div>
    </div>
  );
}

const roleBadge = (color, bg) => ({ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color, background: bg, borderRadius: 9999, padding: '1px 7px' });
const fg  = { display: 'flex', flexDirection: 'column' };
const lbl = { fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--muted)', marginBottom: 3 };
const inp = { padding: '7px 9px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13, background: '#fff', boxSizing: 'border-box', width: '100%' };
const td  = { padding: '6px 8px', borderBottom: '1px solid var(--line2)', textAlign: 'left' };
const tdr = { padding: '6px 8px', borderBottom: '1px solid var(--line2)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const je    = { display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', color: 'var(--ink)' };
const jeAmt = { fontVariantNumeric: 'tabular-nums', fontWeight: 600 };
